/**
 * The Odds API v4 TypeScript Client
 * Documentation: https://the-odds-api.com/liveapi/guides/v4/
 */

// ============================================
// Types and Interfaces
// ============================================

export interface TheOddsApiSport {
	key: string;
	group: string;
	title: string;
	description: string;
	active: boolean;
	has_outrights: boolean;
}

export interface TheOddsApiOutcome {
	name: string;
	price: number;
	point?: number;
	description?: string;
	multiplier?: number;
}

export interface TheOddsApiMarket {
	key: string;
	last_update: string;
	outcomes: TheOddsApiOutcome[];
}

export interface TheOddsApiBookmaker {
	key: string;
	title: string;
	last_update: string;
	markets: TheOddsApiMarket[];
}

export interface TheOddsApiEventOdds {
	id: string;
	sport_key: string;
	sport_title: string;
	commence_time: string;
	home_team: string;
	away_team: string;
	bookmakers: TheOddsApiBookmaker[];
}

export interface TheOddsApiScore {
	name: string;
	score: string;
}

export interface TheOddsApiEventScore {
	id: string;
	sport_key: string;
	sport_title: string;
	commence_time: string;
	completed: boolean;
	home_team: string;
	away_team: string;
	scores: TheOddsApiScore[] | null;
	last_update: string | null;
}

export interface TheOddsApiScheduledEvent {
	id: string;
	sport_key: string;
	sport_title: string;
	commence_time: string;
	home_team: string;
	away_team: string;
}

export interface TheOddsApiEventMarket {
	bookmaker: string;
	markets: string[];
}

export interface TheOddsApiParticipant {
	id: string;
	name: string;
}

export interface TheOddsApiHistoricalOddsResponse {
	timestamp: string;
	previous_timestamp: string | null;
	next_timestamp: string | null;
	data: TheOddsApiEventOdds[];
}

export interface TheOddsApiHistoricalEventsResponse {
	timestamp: string;
	previous_timestamp: string | null;
	next_timestamp: string | null;
	data: TheOddsApiScheduledEvent[];
}

export interface TheOddsApiHistoricalEventOddsResponse {
	timestamp: string;
	previous_timestamp: string | null;
	next_timestamp: string | null;
	data: TheOddsApiEventOdds | null;
}

// ============================================
// Client Configuration
// ============================================

export interface TheOddsApiClientOptions {
	api_key: string;
	base_url?: string;
	timeout_ms?: number;
}

// ============================================
// Client Implementation
// ============================================

export class TheOddsApiClient {
	private readonly api_key: string;
	private readonly base_url: string;
	private readonly timeout_ms: number;

	constructor(options: TheOddsApiClientOptions) {
		this.api_key = options.api_key;
		this.base_url = options.base_url || "https://api.the-odds-api.com/v4";
		this.timeout_ms = options.timeout_ms || 30000;
	}

	private async request<T>(
		path: string,
		query?: Record<string, string | number | boolean>
	): Promise<T> {
		const url = new URL(`${this.base_url}${path}`);
		url.searchParams.set("apiKey", this.api_key);

		if (query) {
			for (const [key, value] of Object.entries(query)) {
				if (value !== undefined && value !== null) {
					url.searchParams.set(key, String(value));
				}
			}
		}

		const controller = new AbortController();
		const timeout_id = setTimeout(() => controller.abort(), this.timeout_ms);

		try {
			const response = await fetch(url.toString(), {
				signal: controller.signal,
			});

			clearTimeout(timeout_id);

			if (!response.ok) {
				const body = await response.text().catch(() => "");
				const body_snippet = body.substring(0, 200);
				throw new Error(
					`Odds API request failed: ${response.status} ${response.statusText}. Body: ${body_snippet}`
				);
			}

			return (await response.json()) as T;
		} catch (error) {
			clearTimeout(timeout_id);
			if (error instanceof Error && error.name === "AbortError") {
				throw new Error(`Odds API request timeout after ${this.timeout_ms}ms`);
			}
			throw error;
		}
	}

	async getSports(options?: { all?: boolean }): Promise<TheOddsApiSport[]> {
		const query: Record<string, boolean> = {};
		if (options?.all) {
			query.all = true;
		}
		return this.request<TheOddsApiSport[]>("/sports", query);
	}

	async getOdds(params: {
		sport: string;
		regions: string;
		markets?: string;
		oddsFormat?: "american" | "decimal";
		dateFormat?: "iso" | "unix";
		bookmakers?: string;
		eventIds?: string;
	}): Promise<TheOddsApiEventOdds[]> {
		const query: Record<string, string> = {
			regions: params.regions,
		};

		if (params.markets) query.markets = params.markets;
		if (params.oddsFormat) query.oddsFormat = params.oddsFormat;
		if (params.dateFormat) query.dateFormat = params.dateFormat;
		if (params.bookmakers) query.bookmakers = params.bookmakers;
		if (params.eventIds) query.eventIds = params.eventIds;

		return this.request<TheOddsApiEventOdds[]>(`/sports/${params.sport}/odds`, query);
	}

	async getScores(params: {
		sport: string;
		daysFrom?: number;
		dateFormat?: "iso" | "unix";
	}): Promise<TheOddsApiEventScore[]> {
		const query: Record<string, string | number> = {};

		if (params.daysFrom) query.daysFrom = params.daysFrom;
		if (params.dateFormat) query.dateFormat = params.dateFormat;

		return this.request<TheOddsApiEventScore[]>(`/sports/${params.sport}/scores`, query);
	}

	async getEvents(params: {
		sport: string;
		dateFormat?: "iso" | "unix";
		eventIds?: string;
		commenceTimeFrom?: string;
		commenceTimeTo?: string;
	}): Promise<TheOddsApiScheduledEvent[]> {
		const query: Record<string, string> = {};

		if (params.dateFormat) query.dateFormat = params.dateFormat;
		if (params.eventIds) query.eventIds = params.eventIds;
		if (params.commenceTimeFrom) query.commenceTimeFrom = params.commenceTimeFrom;
		if (params.commenceTimeTo) query.commenceTimeTo = params.commenceTimeTo;

		return this.request<TheOddsApiScheduledEvent[]>(`/sports/${params.sport}/events`, query);
	}

	async getEventOdds(params: {
		sport: string;
		eventId: string;
		regions: string;
		markets?: string;
		oddsFormat?: "american" | "decimal";
		dateFormat?: "iso" | "unix";
		bookmakers?: string;
	}): Promise<TheOddsApiEventOdds | null> {
		const query: Record<string, string> = {
			regions: params.regions,
		};

		if (params.markets) query.markets = params.markets;
		if (params.oddsFormat) query.oddsFormat = params.oddsFormat;
		if (params.dateFormat) query.dateFormat = params.dateFormat;
		if (params.bookmakers) query.bookmakers = params.bookmakers;

		try {
			return await this.request<TheOddsApiEventOdds>(
				`/sports/${params.sport}/events/${params.eventId}/odds`,
				query
			);
		} catch (error) {
			if (error instanceof Error && error.message.includes("404")) {
				return null;
			}
			throw error;
		}
	}

	async getEventMarkets(params: {
		sport: string;
		eventId: string;
	}): Promise<TheOddsApiEventMarket[]> {
		return this.request<TheOddsApiEventMarket[]>(
			`/sports/${params.sport}/events/${params.eventId}/markets`
		);
	}

	async getParticipants(params: { sport: string }): Promise<TheOddsApiParticipant[]> {
		return this.request<TheOddsApiParticipant[]>(`/sports/${params.sport}/participants`);
	}

	async getHistoricalOdds(params: {
		sport: string;
		date: string;
		regions: string;
		markets?: string;
		oddsFormat?: "american" | "decimal";
		dateFormat?: "iso" | "unix";
		bookmakers?: string;
		eventIds?: string;
	}): Promise<TheOddsApiHistoricalOddsResponse> {
		const query: Record<string, string> = {
			date: params.date,
			regions: params.regions,
		};

		if (params.markets) query.markets = params.markets;
		if (params.oddsFormat) query.oddsFormat = params.oddsFormat;
		if (params.dateFormat) query.dateFormat = params.dateFormat;
		if (params.bookmakers) query.bookmakers = params.bookmakers;
		if (params.eventIds) query.eventIds = params.eventIds;

		return this.request<TheOddsApiHistoricalOddsResponse>(
			`/historical/sports/${params.sport}/odds`,
			query
		);
	}

	async getHistoricalEvents(params: {
		sport: string;
		date: string;
		dateFormat?: "iso" | "unix";
	}): Promise<TheOddsApiHistoricalEventsResponse> {
		const query: Record<string, string> = {
			date: params.date,
		};

		if (params.dateFormat) query.dateFormat = params.dateFormat;

		return this.request<TheOddsApiHistoricalEventsResponse>(
			`/historical/sports/${params.sport}/events`,
			query
		);
	}

	async getHistoricalEventOdds(params: {
		sport: string;
		eventId: string;
		date: string;
		regions: string;
		markets?: string;
		oddsFormat?: "american" | "decimal";
		dateFormat?: "iso" | "unix";
		bookmakers?: string;
	}): Promise<TheOddsApiHistoricalEventOddsResponse> {
		const query: Record<string, string> = {
			date: params.date,
			regions: params.regions,
		};

		if (params.markets) query.markets = params.markets;
		if (params.oddsFormat) query.oddsFormat = params.oddsFormat;
		if (params.dateFormat) query.dateFormat = params.dateFormat;
		if (params.bookmakers) query.bookmakers = params.bookmakers;

		return this.request<TheOddsApiHistoricalEventOddsResponse>(
			`/historical/sports/${params.sport}/events/${params.eventId}/odds`,
			query
		);
	}
}

export function createTheOddsApiClient(options: TheOddsApiClientOptions): TheOddsApiClient {
	return new TheOddsApiClient(options);
}
